"use client";

import { useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { startChat } from "@/lib/support-chat/api";

type Props = {
  agentsOnline: boolean;
  onStarted: (result: {
    ticketId: number;
    sessionToken: string;
    stage: string;
  }) => void;
};

export function StartForm({ agentsOnline, onStarted }: Props) {
  const t = useTranslations("support_chat");
  const locale = useLocale() as "de" | "en";
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const emailRequired = !agentsOnline;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!message.trim()) return setError(t("err_message_required"));
    if (emailRequired && !email.trim()) return setError(t("err_email_required_offline"));
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      return setError(t("err_email_invalid"));
    }
    setSubmitting(true);
    try {
      const result = await startChat({
        message: message.trim(),
        name: name.trim() || undefined,
        email: email.trim() || undefined,
        locale,
      });
      onStarted({
        ticketId: result.ticketId,
        sessionToken: result.sessionToken,
        stage: result.stage,
      });
    } catch (err: any) {
      setError(err?.message || t("err_send_failed"));
    } finally {
      setSubmitting(false);
    }
  }

  const inputClass =
    "block w-full bg-white dark:bg-neutral-900 px-3 py-2 rounded-md border border-neutral-200 dark:border-neutral-800 text-sm text-black dark:text-white placeholder:text-neutral-400 focus:ring-2 focus:ring-red-500 focus:outline-none";

  return (
    <form onSubmit={submit} className="flex flex-col gap-3 p-4">
      <p className="text-sm text-neutral-600 dark:text-neutral-400">
        {agentsOnline ? t("intro_online") : t("intro_offline")}
      </p>

      <div className="grid grid-cols-2 gap-2">
        <input
          type="text"
          placeholder={t("name_placeholder")}
          value={name}
          onChange={(e) => setName(e.target.value)}
          className={inputClass}
        />
        <input
          type="email"
          placeholder={
            emailRequired
              ? t("email_required_placeholder")
              : t("email_optional_placeholder")
          }
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required={emailRequired}
          className={inputClass}
        />
      </div>

      <textarea
        placeholder={t("first_message_placeholder")}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        rows={4}
        className={inputClass + " resize-none"}
      />

      {error && (
        <div className="text-xs text-red-600 dark:text-red-400">{error}</div>
      )}

      <button
        type="submit"
        disabled={submitting}
        className="inline-flex items-center justify-center px-4 py-2 rounded-md bg-red-500 hover:bg-red-600 text-white text-sm font-medium disabled:opacity-50"
      >
        {submitting ? t("sending") : t("start_chat")}
      </button>
    </form>
  );
}
